import { useHasRole } from '@kss/auth';
import { useFeature } from './useFeature';

export type ExportGateReason = 'role' | 'plan' | null;

export interface ExportGate {
  /** True when role + feature gates both pass. While the feature gate loads, returns true (permissive). */
  enabled: boolean;
  /** When `enabled` is false, identifies which gate blocked: 'role' takes precedence over 'plan'. */
  reason: ExportGateReason;
  /** Human-readable explanation suitable for inline tooltips / restricted-message captions. */
  message: string;
}

/**
 * Composite gate for analytics-style export buttons (Excel / PDF / Print / CSV).
 *
 * Combines:
 * - role check via `useHasRole(roles)` — "is this user permitted to act?"
 * - plan-feature check via `useFeature('analytics.export')` — "does the tenant's
 *   subscription include exports?"
 *
 * Both must pass for `enabled: true`. Role failure takes precedence (so an
 * unprivileged user always sees the role message regardless of plan state).
 *
 * Usage:
 * ```ts
 * const exportGate = useExportGate(['admin', 'manager']);
 * if (!exportGate.enabled) return <p>{exportGate.message}</p>;
 * ```
 */
export function useExportGate(roles: string | string[]): ExportGate {
  const hasRole = useHasRole(roles);
  const feature = useFeature('analytics.export');

  if (!hasRole) {
    const list = Array.isArray(roles) ? roles : [roles];
    return {
      enabled: false,
      reason: 'role',
      message: `Export restricted to ${list.join(' / ')} role${list.length > 1 ? 's' : ''}.`,
    };
  }
  if (!feature.enabled) {
    return {
      enabled: false,
      reason: 'plan',
      message: 'Export not included in your current plan — upgrade to enable.',
    };
  }
  return { enabled: true, reason: null, message: '' };
}
